'use client';

import { c as _c } from "react/compiler-runtime";
import { Card, CardContent } from '@achra/ui/card';
import { useMemo } from 'react';
import { type Maybe, RsGroupCostType } from '@/modules/__generated__/graphql/switchboard-generated';
import { BILLING_CYCLE_SUFFIXES } from '@/modules/service-purchase/lib/constants';
import { getGroupPriceFromBreakdown, getPriceBreakdown } from '@/modules/service-purchase/lib/price-breakdown-utils';
import { sortOptionGroups } from '@/modules/service-purchase/lib/utils';
import { useAllOptionGroups, useFacets, usePurchaseTotals, useSelectedBillingCycle, useSelectedTier, useServiceOffering } from '@/modules/service-purchase/providers/service-purchase-store-provider';
import type { PurchaseOptionGroup } from '@/modules/service-purchase/types';
import { SelectedFacets } from './selected-facets';
import { SummaryCardHeader } from './summary-card-header';
import { Summary } from './summary-section';
interface SummaryCardProps {
  templateTitle?: string;
  templateSubtitle?: Maybe<string>;
}
function SummaryCard(t0) {
  const $ = _c(42);
  const {
    templateTitle,
    templateSubtitle
  } = t0;
  const facets = useFacets();
  const totals = usePurchaseTotals();
  const optionGroups = useAllOptionGroups();
  const offering = useServiceOffering();
  const selectedTier = useSelectedTier();
  const selectedBillingCycle = useSelectedBillingCycle();
  let origRecurringSum;
  let recurring;
  let recurringPrices;
  let setup;
  let setupPrices;
  if ($[0] !== offering || $[1] !== optionGroups || $[2] !== selectedBillingCycle || $[3] !== selectedTier.id) {
    const selected = optionGroups.filter(_temp);
    const activeGroupIds = new Set(selected.map(_temp2));
    const breakdown = getPriceBreakdown(offering, selectedTier.id, selectedBillingCycle, activeGroupIds);
    const hasServicesForTier = _temp3;
    let t1;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = group_0 => group_0.costType === RsGroupCostType.Recurring && hasServicesForTier(group_0);
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    recurring = sortOptionGroups(selected.filter(t1));
    setup = sortOptionGroups(selected.filter(group_1 => {
      if (group_1.costType === RsGroupCostType.Setup) {
        return hasServicesForTier(group_1) || group_1.resolvedPrice > 0;
      }
      if (group_1.costType === RsGroupCostType.Recurring && group_1.isAddOn) {
        const setupPrice = getGroupPriceFromBreakdown(breakdown, group_1.id, true);
        return setupPrice != null && setupPrice.amount > 0;
      }
      return false;
    }));
    recurringPrices = new Map();
    origRecurringSum = 0;
    for (const g_1 of recurring) {
      const price = getGroupPriceFromBreakdown(breakdown, g_1.id, false);
      if (price) {
        recurringPrices.set(g_1.id, {
          amount: price.amount,
          originalAmount: price.originalAmount,
          discountPercent: price.discountPercent
        });
        origRecurringSum = origRecurringSum + price.originalAmount;
      }
    }
    setupPrices = new Map();
    for (const g_2 of setup) {
      const price_0 = getGroupPriceFromBreakdown(breakdown, g_2.id, true);
      if (price_0) {
        setupPrices.set(g_2.id, {
          amount: price_0.amount,
          originalAmount: price_0.originalAmount,
          discountPercent: price_0.discountPercent
        });
      }
    }
    $[0] = offering;
    $[1] = optionGroups;
    $[2] = selectedBillingCycle;
    $[3] = selectedTier.id;
    $[4] = origRecurringSum;
    $[5] = recurring;
    $[6] = recurringPrices;
    $[7] = setup;
    $[8] = setupPrices;
  } else {
    origRecurringSum = $[4];
    recurring = $[5];
    recurringPrices = $[6];
    setup = $[7];
    setupPrices = $[8];
  }
  let t1;
  if ($[10] !== origRecurringSum || $[11] !== recurring || $[12] !== recurringPrices || $[13] !== setup || $[14] !== setupPrices) {
    t1 = {
      recurringGroups: recurring,
      setupGroups: setup,
      recurringGroupPrices: recurringPrices,
      setupGroupPrices: setupPrices,
      originalRecurringTotal: origRecurringSum
    };
    $[10] = origRecurringSum;
    $[11] = recurring;
    $[12] = recurringPrices;
    $[13] = setup;
    $[14] = setupPrices;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  const {
    recurringGroups,
    setupGroups,
    recurringGroupPrices,
    setupGroupPrices,
    originalRecurringTotal
  } = t1;
  let t2;
  if ($[16] !== templateSubtitle || $[17] !== templateTitle) {
    t2 = <SummaryCardHeader templateTitle={templateTitle} templateSubtitle={templateSubtitle} />;
    $[16] = templateSubtitle;
    $[17] = templateTitle;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  let t3;
  if ($[19] !== facets.length) {
    t3 = facets.length > 0 && <SelectedFacets />;
    $[19] = facets.length;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  let t4;
  if ($[21] !== originalRecurringTotal || $[22] !== recurringGroupPrices || $[23] !== recurringGroups || $[24] !== selectedBillingCycle || $[25] !== selectedTier || $[26] !== totals) {
    t4 = recurringGroups.length > 0 && <Summary.Provider sectionLabel="Recurring" totalSuffix={BILLING_CYCLE_SUFFIXES[selectedBillingCycle]} groupPrices={recurringGroupPrices} isCustomPricing={selectedTier.isCustomPricing} currency={selectedTier.pricing.currency}><Summary.Card><Summary.Header /><Summary.Content>{recurringGroups.map(_temp4)}</Summary.Content><Summary.Total totalAmount={totals.recurringTotal} originalTotalAmount={originalRecurringTotal} /></Summary.Card></Summary.Provider>;
    $[21] = originalRecurringTotal;
    $[22] = recurringGroupPrices;
    $[23] = recurringGroups;
    $[24] = selectedBillingCycle;
    $[25] = selectedTier;
    $[26] = totals;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  let t5;
  if ($[28] !== selectedTier || $[29] !== setupGroupPrices || $[30] !== setupGroups || $[31] !== totals) {
    t5 = setupGroups.length > 0 && <Summary.Provider sectionLabel="ONE-TIME FEE" totalSuffix="" groupPrices={setupGroupPrices} currency={selectedTier.pricing.currency}><Summary.Card><Summary.Header /><Summary.Content>{setupGroups.map(_temp5)}</Summary.Content>{setupGroups.length > 1 && <Summary.Total totalAmount={totals.setupTotal} />}</Summary.Card></Summary.Provider>;
    $[28] = selectedTier;
    $[29] = setupGroupPrices;
    $[30] = setupGroups;
    $[31] = totals;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] !== t4 || $[34] !== t5) {
    t6 = <div className="flex flex-col gap-4 px-3 lg:px-6">{t4}{t5}</div>;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
  } else {
    t6 = $[35];
  }
  let t7;
  if ($[36] !== t3 || $[37] !== t6) {
    t7 = <CardContent className="flex flex-col gap-6 p-0! pb-3! lg:pb-6!">{t3}{t6}</CardContent>;
    $[36] = t3;
    $[37] = t6;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== t2 || $[40] !== t7) {
    t8 = <Card className="mx-auto w-full max-w-218.5 border-none p-0! lg:mx-0 lg:max-w-none">{t2}{t7}</Card>;
    $[39] = t2;
    $[40] = t7;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  return t8;
}
function _temp5(group_3) {
  return <Summary.Group key={group_3.id} group={group_3} />;
}
function _temp4(group_2) {
  return <Summary.Group key={group_2.id} group={group_2} />;
}
function _temp3(group) {
  return group.services.length > 0;
}
function _temp2(g_0) {
  return g_0.id;
}
function _temp(g) {
  return g.isSelected;
}
export { SummaryCard };